import { useQuery } from "@tanstack/react-query";
import { resolveStorefrontOrgSlug } from "@/lib/api";
import { getStorefrontBootstrap } from "@/lib/storefront-bootstrap";
import { parseStorefrontTrust, type StorefrontTrust } from "@/lib/storefront-trust";

export type PaymentMode = "COD_ONLY" | "PREPAY_ONLY" | "BOTH";

export interface StorefrontConfig {
  allowsDelivery: boolean;
  allowsPickup: boolean;
  paymentMode: PaymentMode;
  // Sprint 12 Slice 2 — interruptor del dueño. Si false, el storefront
  // oculta la sección "Cuenta mayorista" en /account.
  wholesaleEnabled: boolean;
  // Slice 11 — nombre público (publicName u Organization.name) y lo que la
  // tienda puede afirmar: sellos derivados, políticas editadas, contacto.
  storeName: string | null;
  trust: StorefrontTrust;
  // Slice 13b — productos visibles con al menos una reseña.
  ratedProductCount: number;
}

type BootstrapPayload = {
  organization?: { name?: unknown };
  storefront?: Partial<StorefrontConfig> & Record<string, unknown>;
};

interface BootstrapEnvelope extends BootstrapPayload {
  data?: BootstrapPayload;
}

async function fetchStorefrontConfig(): Promise<StorefrontConfig> {
  const res = await getStorefrontBootstrap<BootstrapEnvelope>();
  const payload: BootstrapPayload = res.data?.data ?? res.data ?? {};
  const sf = payload.storefront ?? {};
  const name = payload.organization?.name;
  return {
    allowsDelivery: Boolean(sf.allowsDelivery),
    allowsPickup: Boolean(sf.allowsPickup),
    paymentMode: (sf.paymentMode as PaymentMode) ?? "COD_ONLY",
    wholesaleEnabled: Boolean(sf.wholesaleEnabled),
    storeName: typeof name === "string" && name.trim() ? name.trim() : null,
    trust: parseStorefrontTrust(sf),
    ratedProductCount:
      typeof sf.ratedProductCount === "number" ? sf.ratedProductCount : 0,
  };
}

/**
 * Slice 13b — con menos de 3 productos con reseñas, "Mejor valorados", el
 * filtro y el orden por reseñas no aportan (mostrarían productos sin valorar).
 */
export const MIN_RATED_PRODUCTS_TO_SHOW_RATINGS = 3;

export function useShowRatings(): boolean {
  const { data } = useStorefrontConfig();
  return (data?.ratedProductCount ?? 0) >= MIN_RATED_PRODUCTS_TO_SHOW_RATINGS;
}

export function useStorefrontConfig() {
  const slug = resolveStorefrontOrgSlug();
  return useQuery({
    queryKey: ["storefront-bootstrap", slug ?? "unresolved"],
    queryFn: fetchStorefrontConfig,
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });
}
